import type { Me } from "@contract/firm/me";
import type { FirmStandardsRecord } from "@contract/standards/standards";
import { Button, CloseButton, Group, Modal, Stack, Text, Title } from "@mantine/core";
import * as React from "react";
import { track } from "../../analytics/analytics";
import { ApiError } from "../../api/client";
import { renameFirm } from "../../api/me";
import * as standardsApi from "../../api/standards";
import { SecondaryButton } from "../../ui/buttons";
import { Notice } from "../../ui/Notice";
import { PageLoader } from "../../ui/PageLoader";
import { PANEL_COLOR, PANEL_RADIUS, PANEL_SPACE } from "../../ui/tokens";
import { firmNamed } from "../homeModel";
import { draftProblem, fromDraft, toDraft, type StandardsDraft } from "../standardsModel";
import { browserLocales, firmNameToSave, ONBOARDING_STEPS, welcomeTitle, withPrefill, type OnboardingStep } from "./onboardingModel";
import { ColorsStep, FontsStep, LabelsStep, NameStep, SignInStep, StepNote } from "./onboardingSteps";
import { useSignInWatch } from "./useSignInWatch";

type Api = { standards: typeof standardsApi; rename: typeof renameFirm };
const LIVE: Api = { standards: standardsApi, rename: renameFirm };

/** The card's width and padding, as the signed-out card (frames 15185:5272 and on). */
const CARD = { width: 450, padding: 30 };

const NOTE: Partial<Record<OnboardingStep, string>> = {
  colors: "If you're not sure, that's fine. Leave our default colors for now and add yours later.",
  fonts: "If you're not sure, that's fine. Leave our default fonts for now and add yours later.",
  labels: "If you're not sure, that's fine. Leave our default labels for now and add yours later.",
  signIn: "Your settings are saved. Get SlideBrain and then sign in with the same email address.",
};

function titleOf(step: OnboardingStep, userName: string | null, company: string): string {
  switch (step) {
    case "name":
      return welcomeTitle(userName);
    case "colors":
      return `What colors does ${company} use in PowerPoint?`;
    case "fonts":
      return `What fonts does ${company} use in PowerPoint?`;
    case "labels":
      return `How does ${company} label things in PowerPoint?`;
    case "signIn":
      return "You're all set up! Let's add SlideBrain to your PowerPoint and sign in.";
  }
}

/**
 * The onboarding wizard (A8, Dan's frames 15185:5272 to 15185:5898): the firm's name, its
 * colors, fonts and labels, then AppSource and a live check for the add-in's sign-in. Each
 * Continue saves its step (the name through `PUT /v1/firm/name`, the rest through the firm's
 * standards with the loaded version), so leaving keeps what was done. The colors and fonts
 * open pre-filled and save as they stand (Dan 2026-10-02). `onClose(finished)` runs on Skip
 * for now, the X and Finish.
 */
export function OnboardingWizard({
  me,
  email,
  userName,
  onClose,
  api = LIVE,
}: {
  me: Me;
  email: string;
  userName: string | null;
  onClose: (finished: boolean) => void;
  api?: Api;
}) {
  const [index, setIndex] = React.useState(0);
  const step = ONBOARDING_STEPS[index]!;
  const [name, setName] = React.useState(firmNamed(me) ? me.orgName : "");
  const [company, setCompany] = React.useState(firmNamed(me) ? me.orgName : "");
  const [record, setRecord] = React.useState<FirmStandardsRecord | null>(null);
  const [draft, setDraft] = React.useState<StandardsDraft | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const watch = useSignInWatch({ active: step === "signIn", alreadySignedIn: me.pluginSignedIn, onSignedIn: () => track("web_onboarding_signed_in") });

  React.useEffect(() => {
    let alive = true;
    api.standards
      .fetchFirmStandards()
      .then((r) => {
        if (!alive) return;
        setRecord(r);
        setDraft(withPrefill(toDraft(r), browserLocales()));
      })
      .catch((e: unknown) => alive && setLoadError(e instanceof ApiError ? e.message : "We couldn't load your settings. Try again."));
    return () => {
      alive = false;
    };
  }, []);

  const patch = (partial: Partial<StandardsDraft>) => setDraft((d) => (d ? { ...d, ...partial } : d));
  const go = (next: number) => {
    setError(null);
    setIndex(next);
  };

  const saveStandards = async (): Promise<boolean> => {
    if (!draft || !record) return false;
    const problem = draftProblem(draft);
    if (problem) {
      setError(problem);
      return false;
    }
    const saved = await api.standards.saveFirmStandards({ ...fromDraft(draft), version: record.version });
    setRecord(saved);
    return true;
  };

  const advance = async () => {
    setSaving(true);
    setError(null);
    try {
      if (step === "name") {
        const saved = firmNameToSave(name);
        await api.rename(saved);
        setCompany(saved);
      } else if (step !== "signIn" && !(await saveStandards())) {
        return;
      }
      track("web_onboarding_step_saved", { step });
      go(index + 1);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "We couldn't save that. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const close = (finished: boolean) => {
    track(finished ? "web_onboarding_finished" : "web_onboarding_skipped", { step });
    onClose(finished);
  };

  const last = step === "signIn";
  const note = NOTE[step];
  return (
    <Modal
      opened
      onClose={() => close(false)}
      withCloseButton={false}
      size={CARD.width}
      radius={PANEL_RADIUS.button}
      styles={{ body: { padding: CARD.padding } }}
      aria-label="Set up SlideBrain"
    >
      <CloseButton onClick={() => close(false)} aria-label="Close" pos="absolute" top={12} right={12} />
      <Stack gap={PANEL_SPACE.page}>
        <Stack gap={PANEL_SPACE.field} align="center">
          <Text fw={600} c={PANEL_COLOR.brand} lh="normal">
            Step {index + 1} of {ONBOARDING_STEPS.length}
          </Text>
          <Title order={2} fz={22} fw={600} ta="center" lh="normal" style={{ letterSpacing: "-0.02em" }}>
            {titleOf(step, userName, company || "your firm")}
          </Title>
        </Stack>
        {note ? <StepNote>{note}</StepNote> : null}

        {loadError && step !== "name" ? <Notice tone="danger">{loadError}</Notice> : null}
        {step === "name" ? <NameStep name={name} onName={setName} onSubmit={() => void advance()} /> : null}
        {step !== "name" && step !== "signIn" && !draft && !loadError ? <PageLoader /> : null}
        {step === "colors" && draft ? <ColorsStep draft={draft} patch={patch} /> : null}
        {step === "fonts" && draft ? <FontsStep draft={draft} patch={patch} /> : null}
        {step === "labels" && draft ? <LabelsStep draft={draft} patch={patch} /> : null}
        {last ? <SignInStep email={email} watch={watch} /> : null}
        {error ? <Notice tone="danger">{error}</Notice> : null}

        <Group gap={5} grow>
          {index === 0 ? (
            <SecondaryButton onClick={() => close(false)}>Skip for now</SecondaryButton>
          ) : (
            <SecondaryButton onClick={() => go(index - 1)} disabled={saving}>
              Back
            </SecondaryButton>
          )}
          {last ? (
            <Button onClick={() => close(true)}>Finish</Button>
          ) : (
            <Button loading={saving} disabled={step !== "name" && !draft} onClick={() => void advance()}>
              Continue
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
